import {
  BudgetControl,
  BudgetReqAutoCalFields,
  BudgetReqCanManuallyUpdateFields,
  BudgetReqLineItem,
  MemoBudgetReqState,
  WhtNumberTypes,
} from '@shared/models/budget-request.model';

export function parse2Decimal(v: number): number {
  return parseFloat(v.toFixed(2));
}

export function calculateLineItem(item: BudgetReqLineItem) {
  const vat_percent =
    item.vat_percent === 'none' ? 0 : Number(item.vat_percent);

  item.vat_amount = parse2Decimal(
    item.pre_vat_amount * vat_percent * 0.01,
  );
  item.amount_include_vat = parse2Decimal(
    item.pre_vat_amount + item.vat_amount,
  );
}

export function calculateAll(
  items: BudgetReqLineItem[],
): BudgetReqAutoCalFields {
  const result: BudgetReqAutoCalFields = {
    sub_total: 0,
    total_vat_amount: 0,
    total_wht_0_75_amount: 0,
    total_wht_1_amount: 0,
    total_wht_1_5_amount: 0,
    total_wht_2_amount: 0,
    total_wht_3_amount: 0,
    total_wht_5_amount: 0,
    total_wht_10_amount: 0,
    total_wht_15_amount: 0,
    total_net_amount: 0,
  };
  let totalWht = 0;

  items.forEach((item) => {
    result.sub_total += parse2Decimal(item.pre_vat_amount);
    result.total_vat_amount += parse2Decimal(item.vat_amount);

    const whtPercent =
      item.wht_percent === 'none' ? 0 : Number(item.wht_percent);
    const whtAmount = parse2Decimal(
      item.pre_vat_amount * whtPercent * 0.01,
    );
    totalWht += whtAmount;

    if (item.wht_percent === '0.75') {
      result.total_wht_0_75_amount += whtAmount;
    }
    if (item.wht_percent === '1') {
      result.total_wht_1_amount += whtAmount;
    }
    if (item.wht_percent === '1.5') {
      result.total_wht_1_5_amount += whtAmount;
    }
    if (item.wht_percent === '2') {
      result.total_wht_2_amount += whtAmount;
    }
    if (item.wht_percent === '3') {
      result.total_wht_3_amount += whtAmount;
    }
    if (item.wht_percent === '5') {
      result.total_wht_5_amount += whtAmount;
    }
    if (item.wht_percent === '10') {
      result.total_wht_10_amount += whtAmount;
    }
    if (item.wht_percent === '15') {
      result.total_wht_15_amount += whtAmount;
    }
  });

  result.total_net_amount = parse2Decimal(
    result.sub_total + result.total_vat_amount - totalWht,
  );

  Object.entries(result).forEach(([k, v]) => {
    result[k as keyof BudgetReqAutoCalFields] = parse2Decimal(v);
  });

  return result;
}

export function calculateTotalForManualUpdate(
  sub_total: number,
  fields: BudgetReqCanManuallyUpdateFields,
): Pick<MemoBudgetReqState, 'total_net_amount'> {
  let result = sub_total;

  Object.keys(fields).forEach((k) => {
    const key = k as keyof BudgetReqCanManuallyUpdateFields;
    if (key === 'total_vat_amount') {
      result += fields[key];
    } else {
      result -= fields[key];
    }
  });

  return {
    total_net_amount: parse2Decimal(result),
  };
}

export function getSelectedWhtNumberTypes(
  items: BudgetReqLineItem[],
): WhtNumberTypes[] {
  const whtSet = new Set();

  items.forEach((item) => {
    if (item.wht_percent === 'none') {
      return;
    }
    whtSet.add(item.wht_percent);
  });

  const whtList = Array.from(whtSet) as WhtNumberTypes[];

  const sortedWhtList = whtList.sort((a, b) => {
    return Number(a) - Number(b);
  });

  return sortedWhtList;
}

export function calculateBudgetControl(
  item: Partial<MemoBudgetReqState>,
): BudgetControl {
  const budget_item_detail = item.budget_item_detail;

  const total_budget = budget_item_detail?.total_budget || 0;

  const cumulative_spent =
    (budget_item_detail?.pending_approval || 0) +
    (budget_item_detail?.actual || 0);

  const sub_total = item?.sub_total || 0;

  const total_amount_spent = cumulative_spent + sub_total;

  return {
    total_budget,
    cumulative_spent,
    sub_total,
    total_amount_spent,
    remaining_actual: total_budget - total_amount_spent,
  };
}
